import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight, CheckCircle2, Building2, FileText, BadgeDollarSign, Zap,
  ShieldCheck, Clock, TrendingUp, MessageCircle, ChevronDown, Sparkles,
} from "lucide-react";
import PrequalQuiz from "@/components/PrequalQuiz";
import api from "@/lib/api";

const WA_FALLBACK = "523344065965";
const LOGO = "/logo.png";

const SOLUCIONES = [
  { k: "CS", t: "Crédito Simple", d: "Financiamiento directo para capital de trabajo." },
  { k: "CR", t: "Crédito Revolvente", d: "Línea flexible que se adapta a tu flujo." },
  { k: "AF", t: "Arrendamiento Puro y Financiero", d: "Adquiere activos sin descapitalizarte." },
  { k: "F", t: "Factoraje", d: "Convierte cuentas por cobrar en liquidez." },
  { k: "CE", t: "Créditos Especializados", d: "Soluciones para sectores específicos." },
  { k: "AM", t: "A la Medida", d: "Estructuras según las necesidades de tu negocio." },
];

const PASOS = [
  { n: "01", t: "Entendemos tus necesidades", d: "Evaluamos el momento de tu empresa y sus necesidades de financiamiento." },
  { n: "02", t: "Diseñamos la solución", d: "La empatamos con la oferta de +25 instituciones financieras." },
  { n: "03", t: "Te acompañamos", d: "Gestionamos todo de principio a fin hasta que dispones del capital." },
];

const TESTIMONIOS = [
  { n: "María Roldán", r: "CEO, Industria Manufacturera", q: "Nos consiguieron un arrendamiento que nos permitió modernizarnos sin tocar capital de trabajo. En 6 meses aumentamos producción un 40%." },
  { n: "Jorge Lugo", r: "Director General, Distribuidora", q: "Necesitaba dinero en 72 horas para una compra con 35% de descuento. Brillo lo resolvió en 48 horas. Ese descuento fue 2.5 meses de utilidad." },
  { n: "Ana Castillo", r: "CFO, Importadora Comercial", q: "Entendieron nuestro modelo y diseñaron una estructura de pagos alineada a nuestra operación. Eso no lo hace ningún banco." },
];

const FAQS = [
  { q: "¿Cuánto tiempo tarda el proceso?", a: "Propuesta viable entre 48 y 72 horas, con acompañamiento experto en todo el proceso." },
  { q: "¿Esto me cuesta algo?", a: "Cero costo para ti. La institución financiera paga nuestros servicios. Tú recibes asesoría y acompañamiento sin pagar nada extra." },
  { q: "¿Qué pasa si mi Buró no es perfecto?", a: "Tenemos rutas para distintos perfiles. Un Buró imperfecto no es automáticamente un 'no'. Hay instituciones que valoran garantías, flujo y antigüedad." },
  { q: "¿Cuál es el mínimo que puedo solicitar?", a: "Desde $100,000 MXN. Trabajamos con empresas que necesitan capital para crecer de forma estructurada." },
];

const RESULT_UI = {
  alto_potencial: {
    tag: "Perfil con alto potencial", color: "#00C853", icon: CheckCircle2,
    title: "Buenas noticias.",
    body: "Tu perfil tiene características que la mayoría de nuestras instituciones financieras valoran.",
    eta: "Tu asesor Brillo Finance se comunicará contigo en las próximas 2 horas hábiles con opciones concretas.",
  },
  requiere_analisis: {
    tag: "Perfil que requiere análisis", color: "#FFB800", icon: Zap,
    title: "Tu perfil tiene potencial.",
    body: "Hay algunos elementos que queremos revisar contigo personalmente antes de presentarte opciones. Esto no es un 'no'.",
    eta: "Tu asesor te contactará en las próximas 4 horas hábiles.",
  },
  no_califica_aun: {
    tag: "Trabajemos tu perfil", color: "#52525B", icon: TrendingUp,
    title: "Seré directo contigo.",
    body: "Con el perfil que describes hoy, queremos acompañarte para que en 3 a 6 meses tengas el perfil que las mejores instituciones quieren financiar.",
    eta: "Te enviaremos una guía sin costo para preparar tu empresa y calificar.",
  },
};

const Reveal = ({ children, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay }}>
    {children}
  </motion.div>
);

export default function Landing() {
  const [wa, setWa] = useState(WA_FALLBACK);
  const [result, setResult] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const quizRef = useRef(null);
  const resultRef = useRef(null);

  useEffect(() => {
    api.get("/config").then(({ data }) => data.whatsapp && setWa(data.whatsapp)).catch(() => {});
  }, []);

  const scrollToQuiz = () => quizRef.current?.scrollIntoView({ behavior: "smooth" });

  const SectionCTA = ({ testid, label = "Comenzar mi evaluación gratuita", dark = false }) => (
    <div className="flex justify-center mt-10">
      <button data-testid={testid} onClick={scrollToQuiz}
        className={`inline-flex items-center gap-2 font-semibold px-7 py-3.5 transition-colors ${dark ? "bg-white text-[#333333] hover:bg-[#04b0ba] hover:text-white" : "bg-[#04b0ba] hover:bg-[#038f98] text-white"}`}>
        {label} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );

  const handleComplete = (data) => {
    setResult(data);
    requestAnimationFrame(() =>
      setTimeout(() => quizRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80)
    );
  };

  const waText = result
    ? encodeURIComponent(`Hola Brillo Finance, soy ${result.nombre} de ${result.empresa}. Completé la precalificación (producto sugerido: ${result.producto_sugerido}) y quiero revisar mis opciones de crédito.`)
    : encodeURIComponent("Hola Brillo Finance, quiero información sobre financiamiento para mi empresa.");
  const waLink = `https://wa.me/${wa}?text=${waText}`;

  return (
    <div className="min-h-screen bg-[#ffffff] text-[#333333]">
      {/* NAV */}
      <header className="sticky top-0 z-40 bg-[#ffffff]/80 backdrop-blur-md border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <img src={LOGO} alt="Brillo Finance" className="h-9 sm:h-10 w-auto object-contain" data-testid="nav-logo" />
          <button data-testid="nav-cta" onClick={scrollToQuiz} className="hidden sm:inline-flex items-center gap-2 bg-[#333333] text-white text-sm font-semibold px-5 py-2.5 hover:bg-[#04b0ba] transition-colors">
            Evaluar mi negocio <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden grain border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-8 py-12 sm:py-16 relative z-10">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <Reveal>
              <span className="label-caps inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#04b0ba]" /> Financiamiento empresarial · Guadalajara</span>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05] tracking-tight mt-5">
                Precalifica el crédito para tu empresa <span className="text-[#f05c3e]">ahora</span>.
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-xl sm:text-2xl font-semibold text-[#333333] mt-4">
                Sin sucursales, sin fila, sin burocracia.
              </p>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="text-base sm:text-lg text-[#52525B] mt-4 max-w-xl">
                <b className="text-[#333333]">Descubre en 3 minutos si calificas</b> — y con qué condiciones. Comparamos <b className="text-[#333333]">+25 instituciones financieras</b> para estructurar el financiamiento correcto que tu empresa necesita, no el primero disponible. Sin costo para ti. Con propuesta en mano en 48 horas.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="flex flex-wrap items-center gap-3 mt-8">
                <button data-testid="hero-cta" onClick={scrollToQuiz} className="inline-flex items-center gap-2 bg-[#04b0ba] hover:bg-[#038f98] text-white font-semibold px-7 py-4 transition-colors">
                  Iniciar mi evaluación gratuita <ArrowRight className="w-4 h-4" />
                </button>
                <a data-testid="hero-whatsapp" href={waLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-[#333333] hover:bg-[#333333] hover:text-white font-semibold px-7 py-4 transition-colors">
                  <MessageCircle className="w-4 h-4" /> Hablar por WhatsApp
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="flex flex-wrap gap-6 mt-8 text-sm text-[#52525B]">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#00C853]" /> Datos protegidos</span>
                <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#04b0ba]" /> Respuesta &lt; 24 hrs</span>
                <span className="inline-flex items-center gap-1.5"><BadgeDollarSign className="w-4 h-4 text-[#FFB800]" /> 100% sin costo</span>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5 relative">
            <Reveal delay={0.1}>
              <div className="relative border border-[#E4E4E7]">
                <picture>
                  <source srcSet="/hero.webp" type="image/webp" />
                  <img src="/hero.jpg" alt="Empresario exitoso en oficina moderna" width="760" height="1132" decoding="async" className="w-full h-[380px] lg:h-[460px] object-cover object-top" />
                </picture>
                <div className="absolute bottom-0 left-0 right-0 bg-[#333333] text-white grid grid-cols-3 divide-x divide-white/15">
                  <div className="p-4"><div className="font-display text-2xl font-bold">+25</div><div className="text-[11px] text-white/70 leading-tight mt-1">Instituciones</div></div>
                  <div className="p-4"><div className="font-display text-2xl font-bold">1</div><div className="text-[11px] text-white/70 leading-tight mt-1">Solo expediente</div></div>
                  <div className="p-4"><div className="font-display text-2xl font-bold">0%</div><div className="text-[11px] text-white/70 leading-tight mt-1">Costo de gestión</div></div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-[#333333] text-white py-4 overflow-hidden whitespace-nowrap">
        <div className="marquee inline-block">
          {[...Array(2)].map((_, i) => (
            <span key={i} className="text-sm sm:text-base font-semibold uppercase tracking-[0.15em] text-white/80 mx-6">Crédito Simple • Revolvente • Arrendamiento • Factoraje • Capital de Trabajo • Comercio Exterior • Estructuras a la Medida • </span>
          ))}
        </div>
      </div>

      {/* QUIZ / RESULT */}
      <section ref={quizRef} id="precalifica" className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16 scroll-mt-20">
        {!result ? (
          <>
            <div className="text-center mb-8">
              <span className="label-caps">Precalificación · 3 minutos · sin costo</span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">Comienza tu evaluación gratuita</h2>
            </div>
            <PrequalQuiz onComplete={handleComplete} />
          </>
        ) : (
          <ResultCard result={result} ui={RESULT_UI[result.resultado]} waLink={waLink} onReset={() => setResult(null)} refEl={resultRef} />
        )}
      </section>

      {/* BENEFITS */}
      <section className="border-y border-[#E4E4E7] bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-12">
          <div className="grid md:grid-cols-3 gap-px bg-[#E4E4E7] border border-[#E4E4E7]">
            {[
              { i: Building2, t: "Comparamos por ti", d: "No dependes de un solo banco. Analizamos opciones reales de múltiples instituciones para tu caso." },
              { i: FileText, t: "Un solo expediente", d: "No repites el proceso en cada institución. Integramos todo una sola vez." },
              { i: BadgeDollarSign, t: "Cero costo para ti", d: "La institución nos paga; tú te beneficias. Nuestra asesoría no te cuesta nada." },
            ].map((b, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="bg-[#ffffff] p-6 h-full flex items-start gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-sm bg-[#04b0ba]/10 grid place-items-center"><b.i className="w-5 h-5 text-[#04b0ba]" /></div>
                  <div>
                    <div className="font-semibold">{b.t}</div>
                    <p className="text-sm text-[#52525B] mt-1">{b.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <SectionCTA testid="cta-benefits" label="Ver si califico en 3 minutos" />
        </div>
      </section>

      {/* SOLUCIONES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <Reveal><span className="label-caps">La solución</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 mb-6">Ponemos el capital en movimiento</h2></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E4E4E7] border border-[#E4E4E7]">
          {SOLUCIONES.map((s, i) => (
            <Reveal key={s.k} delay={(i % 3) * 0.06}>
              <div className="bg-[#ffffff] p-5 h-full group hover:bg-[#04b0ba] transition-colors duration-300 flex items-start gap-4">
                <div className="shrink-0 w-11 h-11 border border-[#333333] grid place-items-center font-display font-bold text-sm group-hover:border-white group-hover:text-white transition-colors">{s.k}</div>
                <div>
                  <h3 className="font-semibold group-hover:text-white transition-colors">{s.t}</h3>
                  <p className="text-sm text-[#52525B] mt-1 group-hover:text-white/80 transition-colors">{s.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <SectionCTA testid="cta-soluciones" label="Encontrar mi crédito ideal" />
      </section>

      {/* PROCESO */}
      <section className="bg-[#333333] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
          <Reveal><span className="label-caps text-white/60">¿Cómo lo hacemos posible?</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 mb-8">Tres pasos. Cero fricción.</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {PASOS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <div className="border-t border-white/20 pt-5 flex items-start gap-4">
                  <div className="font-display text-4xl font-bold text-[#04b0ba] leading-none shrink-0">{p.n}</div>
                  <div>
                    <h3 className="font-semibold text-lg">{p.t}</h3>
                    <p className="text-sm text-white/60 mt-1">{p.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <SectionCTA testid="cta-proceso" dark label="Empezar ahora — es gratis" />
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <Reveal><span className="label-caps">Casos reales</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 mb-6">Empresarios que tomaron el control</h2></Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIOS.map((t, i) => (
            <Reveal key={t.n} delay={i * 0.1}>
              <figure className="border border-[#E4E4E7] p-6 h-full flex flex-col">
                <blockquote className="text-[#333333] flex-1">"{t.q}"</blockquote>
                <figcaption className="mt-5 pt-5 border-t border-[#E4E4E7] flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#04b0ba] text-white grid place-items-center font-display font-bold text-sm">{t.n.split(" ").map((w) => w[0]).join("")}</div>
                  <div><div className="font-semibold text-sm">{t.n}</div><div className="text-xs text-[#52525B]">{t.r}</div></div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        <SectionCTA testid="cta-testimonios" label="Quiero estos resultados para mi empresa" />
      </section>

      {/* FAQ */}
      <section className="border-t border-[#E4E4E7] bg-[#F4F4F5]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
          <Reveal><span className="label-caps">Preguntas frecuentes</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 mb-6">Todo lo que necesitas saber</h2></Reveal>
          <div className="border border-[#E4E4E7] bg-[#ffffff] divide-y divide-[#E4E4E7]">
            {FAQS.map((f, i) => (
              <div key={i}>
                <button data-testid={`faq-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 text-left p-5 font-semibold hover:bg-[#F4F4F5] transition-colors">
                  {f.q}
                  <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${openFaq === i ? "rotate-180 text-[#04b0ba]" : ""}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-sm text-[#52525B] -mt-1">{f.a}</div>}
              </div>
            ))}
          </div>
          <SectionCTA testid="cta-faq" label="Precalificar mi empresa ahora" />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#f05c3e] text-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight">El capital que tu negocio necesita existe.</h2>
            <p className="text-lg text-white/80 mt-4">La pregunta es si lo vas a tomar tú o alguien más lo tomará primero.</p>
            <button data-testid="final-cta" onClick={scrollToQuiz} className="inline-flex items-center gap-2 bg-white text-[#f05c3e] font-semibold px-8 py-4 mt-8 hover:bg-[#333333] hover:text-white transition-colors">
              Hacer mi evaluación ahora (3 min) <ArrowRight className="w-4 h-4" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#333333] text-white/60">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
          <img src={LOGO} alt="Brillo Finance" loading="lazy" width="160" height="40" className="h-10 w-auto object-contain bg-white p-1.5 rounded-sm inline-block" data-testid="footer-logo" />
          <p className="text-sm mt-4 max-w-2xl">Broker de crédito empresarial para la pequeña y mediana empresa. Conectamos tu negocio con más de 25 instituciones financieras para encontrar la solución óptima de financiamiento.</p>
          <div className="flex flex-wrap items-center gap-6 mt-6 text-sm">
            <a href={waLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#25D366] hover:underline"><MessageCircle className="w-4 h-4" /> WhatsApp +52 33 4406 5965</a>
            <a href="/login" data-testid="footer-admin-link" className="hover:text-white transition-colors">Panel administrativo</a>
          </div>
          <p className="text-xs mt-8 border-t border-white/10 pt-6">Guadalajara, Jalisco, México. Todos los Derechos Reservados. Brillo Finance 2026.</p>
        </div>
      </footer>

      {/* FLOATING WHATSAPP */}
      <a data-testid="floating-whatsapp" href={waLink} target="_blank" rel="noreferrer"
        className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-5 py-3.5 rounded-full shadow-lg hover:scale-105 transition-transform">
        <MessageCircle className="w-5 h-5" /> <span className="hidden sm:inline">WhatsApp</span>
      </a>
    </div>
  );
}

function ResultCard({ result, ui, waLink, onReset }) {
  const Icon = ui.icon;
  return (
    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }} data-testid="quiz-result" className="border border-[#E4E4E7] bg-white">
      <div className="p-8 sm:p-10">
        <div className="inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: `${ui.color}15`, color: ui.color }}>
          <span className="w-2 h-2 rounded-full" style={{ background: ui.color }} /> {ui.tag}
        </div>
        <Icon className="w-12 h-12 mt-6" style={{ color: ui.color }} />
        <h2 className="font-display text-3xl font-bold tracking-tight mt-4">{ui.title}</h2>
        <p className="text-[#52525B] mt-3">{ui.body}</p>

        {result.resultado !== "no_califica_aun" && (
          <div className="mt-6 border border-[#E4E4E7] bg-[#F4F4F5] p-5">
            <span className="label-caps">Producto más probable</span>
            <div className="font-display text-xl font-semibold mt-1">{result.producto_sugerido}</div>
          </div>
        )}

        <div className="mt-6 flex items-start gap-2 text-sm text-[#52525B]">
          <Clock className="w-4 h-4 mt-0.5 shrink-0 text-[#04b0ba]" /> {ui.eta}
        </div>

        <div className="flex flex-wrap gap-3 mt-8">
          <a data-testid="result-whatsapp" href={waLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-3.5 hover:brightness-95 transition-all">
            <MessageCircle className="w-5 h-5" /> Hablar ahora por WhatsApp
          </a>
          <button data-testid="result-reset" onClick={onReset} className="inline-flex items-center gap-2 border border-[#E4E4E7] hover:border-[#333333] font-semibold px-6 py-3.5 transition-colors">
            Volver al inicio
          </button>
        </div>
        <p className="text-xs text-[#52525B] mt-6">Registramos tu información de forma segura. Un asesor de Brillo Finance ya tiene tus datos y te contactará.</p>
      </div>
    </motion.div>
  );
}

